import { Form, FormikProvider, useFormik } from 'formik';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import Button from '@mui/material/Button';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import { useTranslation } from 'react-i18next';
import {
  ProductScheduleTemplate,
  ProductScheduleTemplateItem
} from '../../@types/model/productScheduleTemplate';
import ProductScheduleTemplateItemForm from './ProductScheduleTemplateItemForm';
import { v4 } from 'uuid';

export interface ProductScheduleTemplateFormProps {
  productScheduleTemplate: ProductScheduleTemplate;
  edit: boolean;
  submitHandler: (group: ProductScheduleTemplate) => void;
  loading: boolean;
}

type createOrUpdateProductScheduleTemplateInitialValues = {
  guid: string;
  name: string;
  type: string;
  items: ProductScheduleTemplateItem[];
};

const ProductScheduleTemplateForm = (props: ProductScheduleTemplateFormProps) => {
  const isMountedRef = useIsMountedRef();
  const { t } = useTranslation();

  const createOrUpdateProductScheduleTemplateShchema = Yup.object().shape({
    name: Yup.string().required(t('name_is_required')),
    type: Yup.string().required(t('type_is_required')),
    items: Yup.array().of(
      Yup.object().shape({
        ordinal: Yup.number().required(t('required')),
        product: Yup.string().required(t('required')),
        quantity: Yup.number().required(t('required'))
      })
    )
  });

  const CreateOrUpdateProductScheduleTemplate =
    useFormik<createOrUpdateProductScheduleTemplateInitialValues>({
      initialValues: {
        guid: props.productScheduleTemplate.guid,
        name: props.productScheduleTemplate.name,
        type: props.productScheduleTemplate.type,
        items: props.productScheduleTemplate.items
      },
      validationSchema: createOrUpdateProductScheduleTemplateShchema,
      onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
        try {
          //await chagePassword(values.email, values.password, values.newPassword);
          props.submitHandler({
            guid: props.productScheduleTemplate.guid,
            name: values.name,
            type: values.type,
            items: values.items
          } as ProductScheduleTemplate);
          if (isMountedRef.current) {
            setSubmitting(false);
          }
        } catch (error: any) {
          resetForm();
          if (isMountedRef.current) {
            setSubmitting(false);
          }
        }
      }
    });

  return (
    <FormikProvider value={CreateOrUpdateProductScheduleTemplate}>
      <Form
        autoComplete="off"
        noValidate
        onSubmit={CreateOrUpdateProductScheduleTemplate.handleSubmit}
      >
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              autoComplete={t('name')}
              type="string"
              label={t('name')}
              {...CreateOrUpdateProductScheduleTemplate.getFieldProps('name')}
              error={Boolean(
                CreateOrUpdateProductScheduleTemplate.touched.name &&
                  CreateOrUpdateProductScheduleTemplate.errors.name
              )}
              helperText={
                CreateOrUpdateProductScheduleTemplate.touched.name &&
                CreateOrUpdateProductScheduleTemplate.errors.name
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('schedule_type')}</InputLabel>
              <Select
                {...CreateOrUpdateProductScheduleTemplate.getFieldProps('type')}
                label={t('type')}
                error={Boolean(
                  CreateOrUpdateProductScheduleTemplate.touched.type &&
                    CreateOrUpdateProductScheduleTemplate.errors.type
                )}
                style={{ width: '100%' }}
              >
                <MenuItem value={'by-unit'}>{t('by_unit')}</MenuItem>
              </Select>
              <FormHelperText style={{ color: 'red' }}>
                {CreateOrUpdateProductScheduleTemplate.touched.type &&
                  CreateOrUpdateProductScheduleTemplate.errors.type}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {CreateOrUpdateProductScheduleTemplate.values.items.map(
            (item: ProductScheduleTemplateItem, index: number) => {
              return (
                <Grid
                  item
                  xs={12}
                  md={12}
                  key={item.guid}
                  style={{ marginTop: -10, marginBottom: -10 }}
                >
                  <ProductScheduleTemplateItemForm
                    productScheduleTemplateItem={item}
                    submitHandler={(newItem: ProductScheduleTemplateItem) => {
                      let newItems = CreateOrUpdateProductScheduleTemplate.values.items.map((i) => {
                        if (i.guid === newItem.guid) {
                          return newItem;
                        }
                        return i;
                      });
                      CreateOrUpdateProductScheduleTemplate.setFieldValue('items', newItems);
                    }}
                    deleteHandler={() => {
                      let newItems = CreateOrUpdateProductScheduleTemplate.values.items
                        .filter((i) => {
                          if (i.guid === item.guid) {
                            return false;
                          }
                          return true;
                        })
                        .map((i, index) => {
                          return {
                            ...i,
                            ordinal: index + 1
                          };
                        });
                      CreateOrUpdateProductScheduleTemplate.setFieldValue('items', newItems);
                    }}
                    errors={
                      CreateOrUpdateProductScheduleTemplate?.errors?.items?.length
                        ? CreateOrUpdateProductScheduleTemplate?.errors?.items[index]
                        : false
                    }
                    touched={
                      CreateOrUpdateProductScheduleTemplate?.touched?.items?.length
                        ? CreateOrUpdateProductScheduleTemplate?.touched?.items[index]
                        : false
                    }
                  ></ProductScheduleTemplateItemForm>
                </Grid>
              );
            }
          )}
          <Grid item xs={12} md={12}>
            {/* <FormHelperText style={{ color: 'red', marginBottom: 10 }}>
              {CreateOrUpdateProductScheduleTemplate.touched.items &&
                CreateOrUpdateProductScheduleTemplate.errors.items}
            </FormHelperText> */}
            <Button
              variant="contained"
              onClick={() => {
                CreateOrUpdateProductScheduleTemplate.setFieldValue('items', [
                  ...CreateOrUpdateProductScheduleTemplate.values.items,
                  {
                    guid: v4(),
                    ordinal: CreateOrUpdateProductScheduleTemplate.values.items.length + 1,
                    product: '',
                    quantity: 0
                  } as ProductScheduleTemplateItem
                ]);
              }}
            >
              {t('add_product_type')}
            </Button>
          </Grid>
        </Grid>
        <LoadingButton
          disabled={props.loading}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={CreateOrUpdateProductScheduleTemplate.isSubmitting}
          sx={{ my: 2 }}
        >
          {props.loading ? <CircularProgress size={26} /> : props.edit ? t('update') : t('add')}
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
};

export default ProductScheduleTemplateForm;
